import { z } from "zod";
import { paginationMetaSchema } from "./base-api-response";
import { DEFAULT_PAGE } from "../constants/api.constants";
import { productSingleVariantResponseSchema } from "./product";


export enum ServiceCategory {
    WALKING = "WALKING",
    GROOMING = "GROOMING",
    PRODUCTS = "PRODUCTS",
}

export enum ServiceType {
    SUBSCRIPTION = "SUBSCRIPTION",
    ONE_TIME = "ONE_TIME",
}

export enum RuleStatus {
    DRAFT = "DRAFT",
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE",
}

export enum EarningRuleValueType {
    FLAT = "FLAT",
    PERCENTAGE = "PERCENTAGE",
}

export enum IncentiveType {
    SUNDAY_SHIFT = "SUNDAY_SHIFT",
    ON_TIME_START = "ON_TIME_START",
    COMPLETION_BONUS = "COMPLETION_BONUS",
    RATING_SESSION = "RATING_SESSION",
    RATING_AGGREGATE = "RATING_AGGREGATE",
    FESTIVE = "FESTIVE",
    TIME_SLOT = "TIME_SLOT",
    DISTANCE = "DISTANCE",
    TIP = "TIP",
    TRIAL_CONVERSION = "TRIAL_CONVERSION",
    PERFORMANCE = "PERFORMANCE",
    ONLINE_DAILY = "ONLINE_DAILY",
    FIRST_SESSION = "FIRST_SESSION",
    SESSION_COUNT = "SESSION_COUNT",
}

export enum PenaltyType {
    NO_SHOW = "NO_SHOW",
    CANCELLATION = "CANCELLATION",
    LATE_LEAVE = "LATE_LEAVE",
    RESCHEDULE = "RESCHEDULE",
    UNIFORM_VIOLATION = "UNIFORM_VIOLATION",
    QUALITY_ISSUE = "QUALITY_ISSUE",
    CUSTOMER_DROPOUT = "CUSTOMER_DROPOUT",
}

export enum CalculationBasis {
    SESSION = "SESSION",
    AGGREGATE_WEEKLY = "AGGREGATE_WEEKLY",
    AGGREGATE_MONTHLY = "AGGREGATE_MONTHLY",
    AGGREGATE_CUSTOM = "AGGREGATE_CUSTOM",
}

export const createBasePayRuleRequestDtoSchema = z.object({
    name: z.string().min(1).max(200),
    serviceCategory: z.nativeEnum(ServiceCategory),
    serviceType: z.nativeEnum(ServiceType).optional(),
    countryCode: z.string().length(2),
    cityCode: z.string().optional(),
    subCityCode: z.string().optional(),
    hubCode: z.string().optional(),
    productId: z.string().optional(),
    variantId: z.string().optional(),
    valueType: z.nativeEnum(EarningRuleValueType),
    value: z.number().positive(),
    startsAt: z.number().int().positive(),
    endsAt: z.number().int().positive().optional(),
    description: z.string().optional(),
    status: z.nativeEnum(RuleStatus),
});

export type ICreateBasePayRuleRequest = z.infer<typeof createBasePayRuleRequestDtoSchema>;

export const updateBasePayRuleRequestDtoSchema = z.object({
    status: z.nativeEnum(RuleStatus),
    endsAt: z.number().int().positive().optional(),
});

export type IUpdateBasePayRuleRequest = z.infer<typeof updateBasePayRuleRequestDtoSchema>;

export const basePayRuleResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    serviceCategory: z.nativeEnum(ServiceCategory),
    serviceType: z.nativeEnum(ServiceType).optional(),
    countryCode: z.string(),
    cityCode: z.string().optional(),
    subCityCode: z.string().optional(),
    valueType: z.nativeEnum(EarningRuleValueType),
    value: z.number(),
    startsAt: z.number(),
    endsAt: z.number().optional(),
    status: z.nativeEnum(RuleStatus),
    hubCode: z.string().optional(),
    productId: z.string().optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
    createdBy: z.string(),
    updatedBy: z.string().optional(),
    product: productSingleVariantResponseSchema.nullish(),
});

export type IBasePayRuleResponse = z.infer<typeof basePayRuleResponseSchema>;

export enum RuleFilterBy {
    ACTIVE = "ACTIVE",
    UPCOMING = "UPCOMING",
    EXPIRED = "EXPIRED",
    DRAFT = "DRAFT",
    INACTIVE = "INACTIVE",
}

export const basePayQueryParamsSchema = z.object({
    serviceCategory: z.nativeEnum(ServiceCategory).nullish(),
    serviceType: z.nativeEnum(ServiceType).nullish(),
    cityCode: z.string().nullish(),
    subCityCode: z.string().nullish(),
    hubCode: z.string().nullish(),
    productId: z.string().nullish(),
    status: z.nativeEnum(RuleStatus).nullish(),
    page: z.preprocess(val => (val ? Number(val) : DEFAULT_PAGE), z.number().int().min(1).default(DEFAULT_PAGE)),
    limit: z.preprocess(
        val => (val ? Number(val) : DEFAULT_PAGE),
        z.number().int().min(1).max(100).default(DEFAULT_PAGE),
    ),
    validFrom: z.preprocess(val => (val ? Number(val) : undefined), z.number().int().positive().nullish()),
    validUntil: z.preprocess(val => (val ? Number(val) : undefined), z.number().int().positive().nullish()),
    filterBy: z.nativeEnum(RuleFilterBy).nullish(),
});

export type IBasePayQueryParams = z.infer<typeof basePayQueryParamsSchema>;

export const incentiveQueryParamsSchema = basePayQueryParamsSchema.extend({
    type: z.nativeEnum(IncentiveType).optional(),
    basis: z.nativeEnum(CalculationBasis).optional(),
});

export type IIncentiveQueryParams = z.infer<typeof incentiveQueryParamsSchema>;

export const penaltyQueryParamsSchema = basePayQueryParamsSchema.extend({
    type: z.nativeEnum(PenaltyType).optional(),
    basis: z.nativeEnum(CalculationBasis).optional(),
});

export type IPenaltyQueryParams = z.infer<typeof penaltyQueryParamsSchema>;

export const basePayRulesResponseSchema = z.object({
    items: z.array(basePayRuleResponseSchema),
    meta: paginationMetaSchema,
});

export type IBasePayRulesResponse = z.infer<typeof basePayRulesResponseSchema>;

export const incentiveRuleResponseSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    serviceCategory: z.nativeEnum(ServiceCategory),
    serviceType: z.nativeEnum(ServiceType).nullish(),
    type: z.nativeEnum(IncentiveType),
    basis: z.nativeEnum(CalculationBasis),
    countryCode: z.string(),
    cityCode: z.string().nullish(),
    subCityCode: z.string().nullish(),
    hubCode: z.string().nullish(),
    productId: z.string().nullish(),
    config: z.record(z.unknown()).nullish(),
    valueType: z.nativeEnum(EarningRuleValueType),
    value: z.number(),
    startsAt: z.number(),
    endsAt: z.number().nullish(),
    requiresApproval: z.boolean(),
    status: z.nativeEnum(RuleStatus),
    createdAt: z.number(),
    updatedAt: z.number(),
    createdBy: z.string(),
    updatedBy: z.string().nullish(),
});

export type IIncentiveRuleResponse = z.infer<typeof incentiveRuleResponseSchema>;

export const createIncentiveRuleRequestDtoSchema = z.object({
    name: z.string().min(1).max(200),
    serviceCategory: z.nativeEnum(ServiceCategory),
    serviceType: z.nativeEnum(ServiceType).nullish(),
    type: z.nativeEnum(IncentiveType),
    basis: z.nativeEnum(CalculationBasis),
    countryCode: z.string().length(2),
    cityCode: z.string().nullish(),
    subCityCode: z.string().nullish(),
    hubCode: z.string().nullish(),
    productId: z.string().nullish(),
    config: z.record(z.unknown()).nullish(),
    valueType: z.nativeEnum(EarningRuleValueType),
    value: z.number().positive(),
    startsAt: z.number().int().positive(),
    endsAt: z.number().int().positive().nullish(),
    requiresApproval: z.boolean().default(false),
    description: z.string().nullish(),
    status: z.nativeEnum(RuleStatus)
});

export type ICreateIncentiveRuleRequest = z.infer<typeof createIncentiveRuleRequestDtoSchema>;

export const updateIncentiveRuleRequestDtoSchema = z.object({
    endsAt: z
        .number()
        .int()
        .positive()
        .refine(
            value => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(0, 0, 0, 0);
                const tomorrowTimestamp = Math.floor(tomorrow.getTime());
                return value >= tomorrowTimestamp;
            },
            {
                message: "End date must be at least tomorrow",
            },
        )
        .nullish(),
    status: z.nativeEnum(RuleStatus).nullish(),
});

export type IUpdateIncentiveRuleRequest = z.infer<typeof updateIncentiveRuleRequestDtoSchema>;

export const incentiveRulesResponseSchema = z.object({
    items: z.array(incentiveRuleResponseSchema),
    meta: paginationMetaSchema,
});

export type IIncentiveRulesResponse = z.infer<typeof incentiveRulesResponseSchema>;

export const penaltyRuleResponseSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    serviceCategory: z.nativeEnum(ServiceCategory),
    serviceType: z.nativeEnum(ServiceType).nullish(),
    type: z.nativeEnum(PenaltyType),
    basis: z.nativeEnum(CalculationBasis),
    countryCode: z.string(),
    cityCode: z.string().nullish(),
    subCityCode: z.string().nullish(),
    hubCode: z.string().nullish(),
    productId: z.string().nullish(),
    config: z.record(z.unknown()).nullish(),
    valueType: z.nativeEnum(EarningRuleValueType),
    value: z.number(),
    startsAt: z.number(),
    endsAt: z.number().nullish(),
    requiresApproval: z.boolean(),
    status: z.nativeEnum(RuleStatus),
    createdAt: z.number(),
    updatedAt: z.number(),
    createdBy: z.string(),
    updatedBy: z.string().nullish(),
});

export type IPenaltyRuleResponse = z.infer<typeof penaltyRuleResponseSchema>;

export const penaltyRulesResponseSchema = z.object({
    items: z.array(penaltyRuleResponseSchema),
    meta: paginationMetaSchema,
});

export type IPenaltyRulesResponse = z.infer<typeof penaltyRulesResponseSchema>;

export enum EBasePayUpsertRowAction {
    CREATE = "CREATE",
    END_DATE_AND_CREATE = "END_DATE_AND_CREATE",
}

export const basePayUpsertNewRuleResponseSchema = z.object({
    code: z.string(),
    name: z.string(),
    description: z.string().optional(),
    level: z.string(),
    serviceCategory: z.nativeEnum(ServiceCategory),
    serviceType: z.nativeEnum(ServiceType).optional(),
    countryCode: z.string(),
    cityCode: z.string().optional(),
    subCityCode: z.string().optional(),
    hubCode: z.string().optional(),
    productId: z.string().optional(),
    variantId: z.string().optional(),
    valueType: z.nativeEnum(EarningRuleValueType),
    value: z.number(),
    startsAt: z.number(),
    endsAt: z.number().optional(),
});

export type IBasePayUpsertNewRule = z.infer<typeof basePayUpsertNewRuleResponseSchema>;

export const basePayUpsertRuleToEndDateResponseSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    valueType: z.nativeEnum(EarningRuleValueType),
    value: z.number(),
    startsAt: z.number(),
    currentEndsAt: z.number().optional(),
    newEndsAt: z.number(),
});

export type IBasePayUpsertRuleToEndDate = z.infer<typeof basePayUpsertRuleToEndDateResponseSchema>;

export const basePayUpsertRowResponseSchema = z.object({
    rowNumber: z.number(),
    action: z.nativeEnum(EBasePayUpsertRowAction),
    newRule: basePayUpsertNewRuleResponseSchema,
    rulesToEndDate: z.array(basePayUpsertRuleToEndDateResponseSchema),
    warnings: z.array(z.string()),
});

export type IBasePayUpsertRow = z.infer<typeof basePayUpsertRowResponseSchema>;

export const basePayUpsertErrorResponseSchema = z.object({
    row: z.number(),
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
});

export type IBasePayUpsertError = z.infer<typeof basePayUpsertErrorResponseSchema>;

export const basePayUpsertWarningResponseSchema = z.object({
    row: z.number(),
    code: z.string(),
    message: z.string(),
});

export type IBasePayUpsertWarning = z.infer<typeof basePayUpsertWarningResponseSchema>;

export const basePayUpsertSummaryResponseSchema = z.object({
    totalRows: z.number(),
    toBeEndDated: z.number(),
    toBeCreated: z.number(),
    errorCount: z.number(),
    warningCount: z.number(),
});

export type IBasePayUpsertSummary = z.infer<typeof basePayUpsertSummaryResponseSchema>;

export const basePayUploadPreviewResponseSchema = z.object({
    rows: z.array(basePayUpsertRowResponseSchema),
    errors: z.array(basePayUpsertErrorResponseSchema),
    warnings: z.array(basePayUpsertWarningResponseSchema),
    summary: basePayUpsertSummaryResponseSchema,
});

export type IBasePayUploadPreviewResponse = z.infer<typeof basePayUploadPreviewResponseSchema>;

export const basePayUploadResultResponseSchema = z.object({
    endDated: z.number(),
    created: z.number(),
    rowsProcessed: z.number(),
    endDatedRuleCodes: z.array(z.string()),
    createdRuleCodes: z.array(z.string()),
    warnings: z.array(z.string()),
});

export type IBasePayUploadResultResponse = z.infer<typeof basePayUploadResultResponseSchema>;

export const createPenaltyRuleRequestDtoSchema = z.object({
    name: z.string().min(1).max(200),
    serviceCategory: z.nativeEnum(ServiceCategory),
    serviceType: z.nativeEnum(ServiceType).nullish(),
    type: z.nativeEnum(PenaltyType),
    basis: z.nativeEnum(CalculationBasis),
    countryCode: z.string().length(2),
    cityCode: z.string().nullish(),
    subCityCode: z.string().nullish(),
    hubCode: z.string().nullish(),
    productId: z.string().nullish(),
    config: z.record(z.unknown()).nullish(),
    valueType: z.nativeEnum(EarningRuleValueType),
    value: z.number().positive(),
    startsAt: z.number().int().positive(),
    endsAt: z.number().int().positive().nullish(),
    requiresApproval: z.boolean().default(true),
    status: z.nativeEnum(RuleStatus).nullish(),
    description: z.string().nullish(),
});

export type ICreatePenaltyRuleRequest = z.infer<typeof createPenaltyRuleRequestDtoSchema>;

export const updatePenaltyRuleRequestDtoSchema = z.object({
    endsAt: z
        .number()
        .int()
        .positive()
        .refine(
            value => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(0, 0, 0, 0);
                const tomorrowTimestamp = tomorrow.getTime();
                return value >= tomorrowTimestamp;
            },
            {
                message: "End date must be at least tomorrow",
            },
        )
        .nullish(),
    status: z.nativeEnum(RuleStatus).nullish(),
});

export type IUpdatePenaltyRuleRequest = z.infer<typeof updatePenaltyRuleRequestDtoSchema>;